import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentCanDeactivate } from '@app/can-deactivate.guard';
import { Note } from '@app/models/note.model';
import { Profile } from '@app/models/profile.model';
import { Staff } from '@app/models/staff.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService } from '@app/services/api.service';
import { DialogService } from '@app/services/dialog.service';
import { GpsService } from '@app/services/gps.service';
import { MessageService } from '@app/services/message.service';
import { RecordService } from '@app/services/record.service';
import { ScheduleService } from '@app/services/schedule.service';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { RecordSelectors } from '@app/store/record/record.selectors';
import { Store } from '@ngxs/store';
import { LibNoteService } from '@care-connect-japan/ckweblib';
import { WebviewService } from '@app/services/webview.service';
import { NoteBlobService } from '@app/services/noteblob.service';

@Component({
  selector: 'app-schedule-note-edit',
  templateUrl: './schedule-note-edit.component.html',
})
export class ScheduleNoteEditComponent
  implements OnInit, ComponentCanDeactivate, OnDestroy
{
  staffList!: Staff[];
  schedule!: Note;
  form!: FormGroup;
  disabled!: boolean;
  duplicate!: boolean;
  profile!: Profile | null;

  private canLeave: boolean = false;

  constructor(
    public scheduleService: ScheduleService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private accesslog: AccesslogService,
    private message: MessageService,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private gpsService: GpsService,
    private recordService: RecordService,
    private libNoteService: LibNoteService,
    private webviewService: WebviewService,
    protected noteblobService: NoteBlobService
  ) {}
  ngOnDestroy(): void {
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
  }

  ngOnInit(): void {
    this.profile = this.store.selectSnapshot(AuthSelectors.profile);
    this.webviewService.kidsWarningBeforeChangePageIndicator(true);
    const operation = this.route.snapshot.paramMap.get('operation');
    this.duplicate = operation === 'duplicate';

    this.route.data.subscribe((data) => {
      this.staffList = data['staffList'];
      this.disabled = data['disabled'];

      const { note } = data['note'];
      this.schedule = { ...note };

      this.form = this.scheduleService.setScheduleForm(
        this.profile,
        this.schedule
      );
    });
  }

  canDeactivate() {
    return this.canLeave;
  }

  async onSubmit(): Promise<void> {
    const noteDatas = this.libNoteService.getNoteData();

    if (this.recordService.isValidationError(noteDatas)) return;

    const findNoteData = this.recordService.findNoteData(noteDatas);
    const getStaffName = this.scheduleService.getStaffName(this.staffList);

    const scheduleData = findNoteData(this.schedule);
    const noteid = this.duplicate ? undefined : this.schedule.noteid;
    const operation = this.recordService.getOperation(noteid);
    const note = {
      ...this.schedule,
      ...scheduleData,
      ...this.scheduleService.getFormValue(
        this.form.value,
        getStaffName(this.schedule)
      ),
      noteid,
      dataV2Contents: undefined,
    };
    const displayName = this.accesslog.getDisplayName();
    const _payload = await this.noteblobService.createNoteBlobPayload([{ operation, note, displayName }]);
    this.apiService
      .setNoteList(_payload)
      .subscribe((res) => {
        const errorList = res
          .filter((item) => !!item.error)
          .map((item) => this.message.getErrorMessage(item.error!));

        if (errorList.length > 0) {
          this.dialog.message(errorList.join('\n'));
        } else if (res.length > 0) {
          this.snackBar.open('予定を保存しました');
        }

        if (errorList.length !== res.length) {
          this.canLeave = true;
          this.webviewService.kidsWarningBeforeChangePageIndicator(false);
          const successList = res
            .filter((item) => !item.error)
            .map((item) => item.note);

          const schedule = this.scheduleService.getSchedule(successList);
          if (schedule) {
            this.setGps(schedule);
          }

          const setting = this.store.selectSnapshot(RecordSelectors.setting);
          const path = this.recordService.getBackPage(setting);
          const primary = this.backPage(path);
          this.router.navigate(['..', { outlets: { primary, edit: null } }],
            { queryParamsHandling: 'preserve', queryParams: { 'note-editing': null }, replaceUrl: true });
        }
      });
  }

  private setGps(schedule: Note) {
    if (
      (this.duplicate || this.changedStatus(schedule.resultatr)) &&
      this.scheduleService.isImplementation(schedule.resultatr)
    ) {
      this.gpsService.setNoteGps(schedule).subscribe();
    }
  }

  protected backPage(path: string) {
    return path;
  }

  private changedStatus(resultatr: number) {
    return this.schedule.resultatr !== resultatr;
  }
}
